import { UserInfo } from "@/interfaces/authentication";
import { permissionsToLabelString } from "@/constants/delegationPermissions";
import { useDelegationContext } from "@/hooks/delegation/useDelegationContext";
import { useSwitchDelegation } from "@/hooks/delegation/useSwitchDelegation";
import { useSwitchToSelfSession } from "@/hooks/delegation/useSwitchToSelfSession";
import { ApiRequestError } from "@/api/util";
import Spinner from "@/components/graphics/Spinner";

interface DelegationMenuItemsProps {
  userInfo: UserInfo;
  onClose: () => void;
}

const DelegationMenuItems = ({
  userInfo,
  onClose,
}: DelegationMenuItemsProps) => {
  const { data: context, isLoading } = useDelegationContext();
  const switchMutation = useSwitchDelegation();
  const switchSelfMutation = useSwitchToSelfSession();

  const availableLawyers = context?.availableLawyers ?? [];
  const showSwitchToSelf =
    (context?.canSwitchToSelf ?? false) && userInfo.isDelegatedSession;
  const showDelegation =
    userInfo.isDelegatedSession || availableLawyers.length > 0;
  const isPending = switchMutation.isPending || switchSelfMutation.isPending;

  if (!showDelegation) {
    return null;
  }

  const handleSwitch = async (delegationId: string) => {
    onClose();
    try {
      await switchMutation.mutateAsync({ delegationId });
    } catch (_err) {
      // Errors surface via mutation state; parent can refetch userInfo on success
    }
  };

  const handleSwitchToSelf = async () => {
    onClose();
    try {
      await switchSelfMutation.mutateAsync();
    } catch (_err) {
      // handled by mutation
    }
  };

  const switchError =
    switchMutation.error instanceof ApiRequestError
      ? switchMutation.error.message
      : switchSelfMutation.error instanceof ApiRequestError
        ? switchSelfMutation.error.message
        : switchMutation.error || switchSelfMutation.error
          ? userInfo.isDelegatedSession
            ? "Kunne ikke skifte profil."
            : "Kunne ikke skifte advokat."
          : null;

  return (
    <>
      {switchError ? (
        <li role="none">
          <span
            className="dropdown-item-text text-danger small px-3 py-1"
            role="alert"
          >
            {switchError}
          </span>
        </li>
      ) : null}
      <li role="none">
        <span className="dropdown-header px-3">Skift profil</span>
      </li>
      {isLoading || isPending ? (
        <li role="none" className="px-3 py-2">
          <Spinner inline size={14} label="Skifter profil..." />
        </li>
      ) : null}
      {showSwitchToSelf ? (
        <li role="none">
          <button
            type="button"
            id="delegation-switch-back"
            className="dropdown-item rounded-3"
            role="menuitem"
            onClick={handleSwitchToSelf}
            disabled={isPending || isLoading}
          >
            Tilbage til egen profil
          </button>
        </li>
      ) : null}
      {availableLawyers.map((lawyer) => {
        const isActive = userInfo.delegationId === lawyer.delegationId;
        return (
          <li role="none" key={lawyer.delegationId}>
            <button
              type="button"
              id={`delegation-switch-${lawyer.delegationId}`}
              className={`dropdown-item rounded-3${isActive ? " active" : ""}`}
              role="menuitem"
              onClick={() => handleSwitch(lawyer.delegationId)}
              disabled={isActive || isPending || isLoading}
            >
              <span className="d-block fw-semibold">{lawyer.lawyerName}</span>
              <small className="text-muted">
                {permissionsToLabelString(lawyer.permissions)}
              </small>
            </button>
          </li>
        );
      })}
    </>
  );
};

export default DelegationMenuItems;
